import React from 'react';
import './GraficoDoLogo.css';

/**
 * O gráfico do LOGO, grande e em movimento — o fundo da tela de carga e da
 * tela de login (05/10).
 *
 * ── O que ele substitui ──────────────────────────────────────────────────
 *
 * `GraficoDaMarca`: uma linha em ziguezague, tipo gráfico de bolsa, que se
 * desenhava e ficava parada. Não era o desenho da marca. Saiu primeiro da
 * tela de carga e, no mesmo dia, do login — ela viu as duas telas com fundos
 * diferentes e pediu a mesma animação nas duas.
 *
 * O logo é outra figura, e é ela que mora aqui: TRÊS BARRAS crescendo, uma
 * ONDA suave subindo por cima delas e um PONTO no fim (ver o comentário de
 * `public/icons/favicon.svg`: as barras são os meses, a onda é a projeção, o
 * ponto é onde ela chega).
 *
 * ── Um componente, duas telas ────────────────────────────────────────────
 *
 * O desenho e o movimento são os MESMOS; a variante só decide o tamanho e
 * onde ele fica. Duas cópias do SVG seriam duas marcas para manter iguais.
 *
 *   carga   grande, atrás do nome e do status
 *   login   baixo, no espaço que sobra embaixo do cartão — o cartão é opaco,
 *           e um gráfico maior ficaria com o ponto escondido atrás dele
 *
 * ── Movimento contínuo, e pouco ──────────────────────────────────────────
 *
 * As barras sobem uma vez e depois "respiram"; um brilho percorre a onda; o
 * ponto pulsa. Sempre há algo se mexendo — é isso que diz "estou trabalhando"
 * numa espera longa — mas nada disputa com o texto, que fica na frente.
 *
 * `meet` e não `none`: esticar distorceria o ponto num oval. O desenho fica
 * ancorado embaixo e centralizado, do celular ao monitor largo.
 */
export type VarianteDoGrafico = 'carga' | 'login';

const ONDA = 'M12 236 C 70 236, 78 188, 136 188 S 204 128, 262 128 S 330 46, 388 46';

const GraficoDoLogo: React.FC<{ variante: VarianteDoGrafico }> = ({ variante }) => {
  // Um id por variante: dois gradientes com o mesmo id na página fariam o
  // segundo SVG pintar com o do primeiro (ou com nada, se ele sumir).
  const idBarra = `fc-barra-${variante}`;
  return (
  <svg
    className={`fc fc-${variante}`}
    viewBox="0 0 400 300"
    preserveAspectRatio="xMidYMax meet"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id={idBarra} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0.02" />
      </linearGradient>
    </defs>

    <rect className="fc-barra fc-barra-1" x="46" y="196" width="84" height="104" rx="14" fill={`url(#${idBarra})`} />
    <rect className="fc-barra fc-barra-2" x="158" y="136" width="84" height="164" rx="14" fill={`url(#${idBarra})`} />
    <rect className="fc-barra fc-barra-3" x="270" y="62" width="84" height="238" rx="14" fill={`url(#${idBarra})`} />

    <path className="fc-onda" pathLength={1} d={ONDA} />
    <path className="fc-brilho" pathLength={1} d={ONDA} />

    <circle className="fc-halo" cx="388" cy="46" r="7" />
    <circle className="fc-ponto" cx="388" cy="46" r="5" />
  </svg>
  );
};

export default GraficoDoLogo;
